import React, { useEffect, useState } from "react";
import {
    Box, Grid, Card, CardHeader, Typography, Divider, Chip, Stack,
    Button, Alert, CircularProgress
} from "@mui/material";
import { Download } from "@mui/icons-material";
import MDBox from "common/components/MDBox";
import CommonSelect from "common/components/CustomSelect";
import { useDispatch, useSelector } from "react-redux";
import { getCourseList } from "services/course/course";
import { getIntakeList } from "services/intake/intake";
import { getIncomeReport } from "services/income/income";
import analyze from '../../assets/images/analyze.png'
import PendingRow from "common/components/PendingRow";
import SummaryStatCard from "common/components/SummaryStatCard";
import ProgressCard from "common/components/ProgressCard";

const money = (n) =>
    (n ?? 0).toLocaleString(undefined, { style: "currency", currency: "LKR", maximumFractionDigits: 0 });

const toCSV = (rows) => {
    const header = [
        "student_id",
        "name",
        "phone",
        "email",
        "register_date",
        "amount",
        "discount",
        "expected",
        "total_paid",
        "pending",
    ];
    const body = rows.map((r) => [
        r.student.id,
        r.student.name,
        r.student.phone,
        r.student.email,
        r.register_date,
        r.amount,
        r.discount,
        r.expected,
        r.total_paid,
        r.pending,
    ]);
    const csv = [header, ...body].map((row) => row.map((x) => `"${String(x ?? "").replaceAll('"', '""')}"`).join(",")).join("\n");
    return new Blob([csv], { type: "text/csv;charset=utf-8;" });
};

export default function IncomeReport() {

    const dispatch = useDispatch();
    const [courseOption, setCourseOption] = useState([]);
    const [selectCourse, setSelectCourse] = useState("");
    const [intakeOption, setIntakeOption] = useState([]);
    const [selectIntake, setSelectIntake] = useState("");
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const course = useSelector((state) => state.courseSlice.course);
    const intake = useSelector((state) => state.intakeSlice.intake);
    const incomeData = useSelector((state) => state.incomeReportSlice.income);

    useEffect(() => {
        dispatch(getCourseList());
        dispatch(getIntakeList());
    }, []);

    useEffect(() => {
        if (course?.isSuccess) {
            const arr = [];
            course.data?.data?.forEach((val) => {
                arr.push({ label: val.title, value: val.id });
            });
            setCourseOption(arr);
        }
    }, [course?.data, course?.isSuccess]);

    useEffect(() => {
        if (intake?.isSuccess && selectCourse !== "") {
            const arr = [];
            intake.data?.forEach((val) => {
                if (val.course_id === selectCourse) {
                    arr.push({ label: val.intake, value: val.id });
                }
            });
            setIntakeOption(arr);
        }
    }, [intake?.data, intake?.isSuccess, selectCourse]);

    useEffect(() => {
        if (incomeData?.isSuccess && loading) {
            setReport(incomeData.data);
            setLoading(false)
        } else if (!incomeData?.isSuccess && loading) {
            setError("Failed to load income report");
            setLoading(false)
        }

    }, [incomeData.data])

    const handleExportCSV = () => {
        if (!report?.pending_students) return;
        const blob = toCSV(report.pending_students);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `pending_students_intake_${report?.intake?.intake_id || selectIntake}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const onChangeIntake = (val) => {
        setSelectIntake(val)
        dispatch(getIncomeReport(val))
        setLoading(true);
        setError("");
    }

    return (
        <MDBox pt={6} pb={3}>
            <div className="content">
                <Grid container spacing={3} p={1}>
                    <Grid item xs={12} sm={6} lg={6} md={6} xl={6}>
                        <CommonSelect
                            id="course-select"
                            options={courseOption}
                            value={selectCourse}
                            onChange={(event) => {
                                setSelectCourse(event.target.value);
                                setSelectIntake("");
                                setReport(null);
                            }}
                            label="Select Course"
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} lg={6} md={6} xl={6}>
                        <CommonSelect
                            id="intake-select"
                            options={intakeOption}
                            value={selectIntake}
                            onChange={(event) => {
                                onChangeIntake(event.target.value);
                            }}
                            label="Select Intake"
                        />
                    </Grid>
                </Grid>

                {error && (
                    <Box className="mt-3">
                        <Alert severity="warning">{error}</Alert>
                    </Box>
                )}

                {report && (
                    <Box className="mt-4 space-y-4" p={1}>
                        {/* Intake header */}
                        <Card className="rounded-2xl">
                            <CardHeader
                                title={
                                    <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }}>
                                        <Typography variant="h6" className="font-semibold">
                                            {report.intake.intake_name}
                                        </Typography>
                                        <Stack direction="row" flexWrap={'wrap'} gap={1}>
                                            <Chip label={`Intake ID: ${report.intake.intake_id}`} />
                                            <Chip label={`Started: ${report.intake.started_date}`} variant="outlined" />
                                            <Chip label={`Course Fee: ${money(Number(report.intake.course_fee))}`} color="primary" variant="outlined" />
                                        </Stack>
                                    </Stack>
                                }
                            />
                        </Card>

                        {/* KPI Summary */}
                        <Grid container spacing={2} pt={2}>
                            <Grid item xs={12} sm={6} md={3}>
                                <SummaryStatCard title="Expected Income" value={money(report.summary.expected_income)} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <SummaryStatCard title="Collected (Actual)" value={money(report.summary.actual_income)} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <SummaryStatCard title="Pending Income" value={money(report.summary.pending_income)} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <SummaryStatCard
                                    title="Students"
                                    value={`${report.summary.students_paid_off}/${report.summary.students_total} paid off`}
                                    help={`${report.summary.students_pending} pending`}
                                />
                            </Grid>
                        </Grid>

                        {/* Progress */}
                        <Grid container spacing={2} pt={2}>
                            <Grid item xs={12} md={6}>
                                <ProgressCard
                                    title="Income Collected"
                                    numerator={report.summary.actual_income}
                                    denominator={report.summary.expected_income}
                                    moneyMode
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <ProgressCard
                                    title="Students Cleared"
                                    numerator={report.summary.students_paid_off}
                                    denominator={report.summary.students_total}
                                />
                            </Grid>
                        </Grid>

                        {/* Pending students */}
                        <Box pt={2}>
                            <Card className="rounded-2xl">
                                <CardHeader
                                    title={
                                        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "center" }}>
                                            <Typography variant="h6">Pending Students</Typography>
                                            <Stack direction="row" gap={1}>
                                                <Chip variant="outlined" label={`Count: ${report.pending_students.length}`} />
                                                <Button size="small" startIcon={<Download />} sx={{ color: '#ffffff' }} onClick={handleExportCSV} variant="contained">
                                                    Export CSV
                                                </Button>
                                            </Stack>
                                        </Stack>
                                    }
                                />
                                <Divider />
                                <Box sx={{ padding: { xs: 0, sm: 2 } }}>
                                    {report.pending_students.map((row) => (
                                        <PendingRow key={row.student.id} row={row} />
                                    ))}
                                </Box>
                            </Card>
                        </Box>
                    </Box>
                )}
                {!report &&
                    <Box p={1}>
                        <Card sx={{ p: 2, borderRadius: 4, width: '100%', height: '400px', minHeight: '400px', maxHeight: '700px', backgroundColor: '#E1E9C9', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <Box sx={{ textAlign: 'center' }}>

                                {loading ?
                                    <CircularProgress thickness={2} color="success" />
                                    :
                                    <img src={analyze} style={{ width: '100px' }} />
                                }
                                <Typography variant="body2" color="text.secondary" mt={2}>
                                    No report data. Please select a course and intake.
                                </Typography>
                            </Box>

                        </Card>
                    </Box>
                }
            </div>
        </MDBox>
    );
}
